import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

import { useToast } from '@/components/ui/toast';
import { describeFailure } from '@/lib/i18n/errors';
import { attachments as attachmentsApi } from '@/lib/api/endpoints';
import { keys } from '@/lib/queryClient';

/** What the backend accepts. Checked here too, to fail before the round trip. */
export const ACCEPTED_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'application/pdf',
] as const;

export const ACCEPT_ATTRIBUTE = ACCEPTED_MIME_TYPES.join(',');

export function isAcceptedFile(file: File): boolean {
  return (ACCEPTED_MIME_TYPES as readonly string[]).includes(file.type);
}

export function useAttachments(taskId: string) {
  return useQuery({
    queryKey: keys.attachments(taskId),
    queryFn: () => attachmentsApi.list(taskId),
  });
}

export function useUploadAttachment(taskId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (file: File) => attachmentsApi.upload(taskId, file),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.attachments(taskId) });
      void queryClient.invalidateQueries({ queryKey: keys.task(taskId) });
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (error) => {
      toast.error(describeFailure(error, 'error.attachmentUpload'));
    },
  });
}

export function useDeleteAttachment(taskId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (attachmentId: string) => attachmentsApi.remove(taskId, attachmentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.attachments(taskId) });
      void queryClient.invalidateQueries({ queryKey: keys.task(taskId) });
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (error) => {
      toast.error(describeFailure(error, 'error.attachmentDelete'));
    },
  });
}

/**
 * Pull image files out of a paste.
 *
 * This is the headline workflow: `⌘⇧4` to grab a screenshot, then `⌘V` into the
 * task. A screenshot on the clipboard arrives as a `File` with a generic name
 * like "image.png", so it is renamed to something recognisable — an attachment
 * list of six files all called "image.png" is useless.
 */
export function filesFromClipboard(event: ClipboardEvent): File[] {
  const items = event.clipboardData?.items;
  if (!items) return [];

  const files: File[] = [];
  for (const item of items) {
    if (item.kind !== 'file') continue;
    const file = item.getAsFile();
    if (!file || !isAcceptedFile(file)) continue;

    // Names the OS invents for a clipboard paste, in the languages this app
    // runs in. Matching them is what lets a pasted screenshot be renamed to
    // something a person can recognise in a list.
    const looksGeneric =
      /^(image|screenshot|snímek|bildschirmfoto|bildschirmphoto)\b/i.test(file.name) ||
      !file.name;
    files.push(
      looksGeneric
        ? new File([file], generatedName(file.type), { type: file.type })
        : file,
    );
  }
  return files;
}

function generatedName(mimeType: string): string {
  const extension = mimeType.split('/')[1]?.replace('jpeg', 'jpg') ?? 'png';
  const stamp = new Date()
    .toISOString()
    .slice(0, 19)
    .replace('T', '-')
    .replace(/:/g, '');
  return `screenshot-${stamp}.${extension}`;
}

/** Whether the paste landed in something that handles its own input. */
function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'INPUT' ||
    target.isContentEditable
  );
}

/**
 * Handle `⌘V` of an image while something is open.
 *
 * Bound to the document rather than to a specific field, because the natural
 * gesture is to press `⌘V` with the dialog open — not to click into a drop zone
 * first.
 *
 * Pastes that land **inside a text field are ignored**, because that field
 * handles them itself: the description editor uploads the image and writes an
 * `![](…)` at the caret. Without this check both would fire and the same
 * screenshot would be uploaded twice.
 */
export function usePasteFiles(onFiles: (files: File[]) => void, enabled = true) {
  const handler = useCallback(
    (event: ClipboardEvent) => {
      if (isEditableTarget(event.target)) return;

      const files = filesFromClipboard(event);
      if (files.length === 0) return;
      // Only claim the paste once we know it carries a file; a paste with both
      // text and an image should still insert the text.
      event.preventDefault();
      onFiles(files);
    },
    [onFiles],
  );

  useEffect(() => {
    if (!enabled) return;
    document.addEventListener('paste', handler);
    return () => document.removeEventListener('paste', handler);
  }, [handler, enabled]);
}
